import { EditChatTabNames } from '@choco-admin/types';
import { Button } from '@heroui/react';
import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FiCopy } from 'react-icons/fi';
import { useParams, useNavigate } from 'react-router-dom';

import { ErrorCard } from '../../../../components/ErrorCard';
import { PageLoading } from '../../../../components/PageLoading';
import { useGetChatData } from '../../hooks';
import { useActiveTabName } from '../../hooks/useActiveTabName';

import { EditChatHeader, EditChatTabPanels, EditChatTabs } from './components';
import { DuplicateChatModal } from './components/DuplicateChatModal';
import {
  ChatPageContextProvider,
  DeliverySettingContextProvider,
} from './contexts';

export const EditChat: React.FC = () => {
  const { chatId } = useParams<{ chatId: string }>();
  const navigate = useNavigate();
  const activeTabFromUrl = useActiveTabName();
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

  // Determine active tab from URL hash or default to PARTICIPANTS
  const activeTab = Object.values(EditChatTabNames).includes(
    activeTabFromUrl as EditChatTabNames
  )
    ? (activeTabFromUrl as EditChatTabNames)
    : EditChatTabNames.PARTICIPANTS;

  const handleChatError = useCallback((err: Error) => {
    // Error will be handled by the hook and displayed in UI
    // eslint-disable-next-line no-console
    console.error('Failed to load chat:', err);
  }, []);

  const { chatData, isLoading, error, refetch } = useGetChatData(chatId || '', {
    onError: handleChatError,
  });

  const pageTitle = chatData
    ? `${chatData.restaurantName} / ${
        chatData.supplierEntity?.name || 'Unknown Supplier'
      } - Choco Admin`
    : 'Edit Chat - Choco Admin';

  // Set default tab if no hash is present (like the old implementation)
  useEffect(() => {
    if (!activeTabFromUrl && chatId) {
      navigate(`#${EditChatTabNames.PARTICIPANTS}`, { replace: true });
    }
  }, [activeTabFromUrl, chatId, navigate]);

  // Handle missing chatId
  if (!chatId) {
    return (
      <div className="p-6 min-h-screen bg-background">
        <ErrorCard
          title="Invalid Chat ID"
          message="No chat ID provided in the URL."
          action={{
            label: 'Back to Chats',
            onPress: () => navigate('/chats'),
            color: 'primary',
          }}
        />
      </div>
    );
  }

  // Handle loading state
  if (isLoading) {
    return <PageLoading text="Loading chat details..." />;
  }

  // Handle error state
  if (error || !chatData) {
    return (
      <div className="p-6 min-h-screen bg-background">
        <ErrorCard
          title="Failed to Load Chat"
          message={error?.message || 'Unable to load chat details.'}
          action={
            <div className="flex gap-3">
              <Button variant="bordered" onPress={() => navigate('/chats')}>
                Back to Chats
              </Button>
              <Button
                color="primary"
                onPress={() => refetch()}
                isLoading={isLoading}
              >
                Try Again
              </Button>
            </div>
          }
        />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <div className="p-6 min-h-screen bg-background">
        <DeliverySettingContextProvider>
          <ChatPageContextProvider initialChatData={chatData}>
            <div className="max-w-7xl mx-auto">
              {/* Header */}
              <div className="flex items-center justify-between">
                <EditChatHeader chatData={chatData} chatId={chatId} />
                <Button
                  size="sm"
                  variant="flat"
                  color="primary"
                  startContent={<FiCopy className="w-4 h-4" />}
                  onPress={() => setIsDuplicateModalOpen(true)}
                >
                  Duplicate Chat
                </Button>
              </div>

              {/* Duplicate Chat Modal */}
              <DuplicateChatModal
                isOpen={isDuplicateModalOpen}
                onClose={() => setIsDuplicateModalOpen(false)}
                chatId={chatId}
                chatData={chatData}
              />

              {/* Tabs Navigation */}
              <div className="mt-6">
                <EditChatTabs activeTab={activeTab} chatData={chatData} />
              </div>

              {/* Tab Content */}
              <div className="mt-6">
                <EditChatTabPanels
                  activeTab={activeTab}
                  chatData={chatData}
                  chatId={chatId}
                />
              </div>
            </div>
          </ChatPageContextProvider>
        </DeliverySettingContextProvider>
      </div>
    </>
  );
};
